/**
 * UI Controls Module
 * Phase 5.1: Rotation speed inputs, angle displays, buttons, projection selector
 *
 * Ported from Python: map_projection/app/cartographer.py lines 44-70
 */

import { emit } from '../core/event-bus.js';

const SIDEBAR_STORAGE_KEY = 'map-projection-sidebar-state';

/**
 * Load sidebar state from localStorage
 * @returns {boolean} true if expanded, false if collapsed
 */
function loadSidebarState() {
    const saved = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    return saved === 'expanded'; // Default to collapsed (false) if not set
}

/**
 * Save sidebar state to localStorage
 * @param {boolean} isExpanded - true if expanded, false if collapsed
 */
function saveSidebarState(isExpanded) {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, isExpanded ? 'expanded' : 'collapsed');
}

/**
 * Toggle sidebar collapsed/expanded state
 * @param {boolean} [forceState] - Optional: true to expand, false to collapse
 * @returns {boolean} New state (true = expanded)
 */
function toggleSidebar(forceState) {
    const container = document.getElementById('sidebar-container');
    const isCurrentlyExpanded = container.classList.contains('sidebar-expanded');

    // Determine new state
    const shouldExpand = forceState !== undefined ? forceState : !isCurrentlyExpanded;

    // Update classes
    if (shouldExpand) {
        container.classList.remove('sidebar-collapsed');
        container.classList.add('sidebar-expanded');
    } else {
        container.classList.remove('sidebar-expanded');
        container.classList.add('sidebar-collapsed');
    }

    // Save preference
    saveSidebarState(shouldExpand);

    return shouldExpand;
}

/**
 * Initialize UI controls and wire up event handlers
 * @param {Object} app - Application state from main.js
 * @returns {Object} controls - References to control elements
 */
export function initControls(app) {
    const controls = {
        // Input elements
        speedAlpha: document.getElementById('speed-alpha'),
        speedBeta: document.getElementById('speed-beta'),
        speedGamma: document.getElementById('speed-gamma'),

        // Display element (combined)
        anglesCombined: document.getElementById('angles-combined'),

        // Buttons
        pauseButton: document.getElementById('btn-pause'),
        resetButton: document.getElementById('btn-reset'),
        randomizeButton: document.getElementById('btn-randomize'),

        // Projection selector buttons
        projectionButtons: document.querySelectorAll('.projection-button'),

        // Sidebar toggle button
        sidebarToggle: document.getElementById('sidebar-toggle'),
        sidebarContainer: document.getElementById('sidebar-container'),
    };

    // Validate numeric input: only allow numbers with optional negative/decimal
    const validateNumericInput = (input) => {
        const value = input.value;

        // Allow empty, "-", ".", "-."
        if (value === '' || value === '-' || value === '.' || value === '-.') {
            return true;
        }

        // Check if valid number
        const num = parseFloat(value);
        if (isNaN(num)) {
            input.classList.add('invalid');
            return false;
        }

        input.classList.remove('invalid');
        return true;
    };

    // Parse input value to float (with fallback to 0)
    const parseInputValue = (input) => {
        const value = input.value;
        if (value === '' || value === '-' || value === '.' || value === '-.') {
            return 0;
        }
        const num = parseFloat(value);
        return isNaN(num) ? 0 : num;
    };

    // Speed input handlers - update on input change
    const updateRotationSpeeds = () => {
        const alphaSpeed = parseInputValue(controls.speedAlpha);
        const betaSpeed = parseInputValue(controls.speedBeta);
        const gammaSpeed = parseInputValue(controls.speedGamma);

        if (app.earthModel) {
            app.earthModel.setRotationSpeeds(alphaSpeed, betaSpeed, gammaSpeed);
        }
    };

    controls.speedAlpha.addEventListener('input', (e) => {
        validateNumericInput(e.target);
        updateRotationSpeeds();
    });

    controls.speedBeta.addEventListener('input', (e) => {
        validateNumericInput(e.target);
        updateRotationSpeeds();
    });

    controls.speedGamma.addEventListener('input', (e) => {
        validateNumericInput(e.target);
        updateRotationSpeeds();
    });

    // Initialize sidebar state from localStorage
    const savedState = loadSidebarState();
    toggleSidebar(savedState);

    // Sidebar toggle button handler
    controls.sidebarToggle.addEventListener('click', () => {
        toggleSidebar();
    });

    // Optional keyboard shortcut (Ctrl/Cmd + B to toggle sidebar)
    document.addEventListener('keydown', (e) => {
        // Ctrl+B or Cmd+B
        if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
            e.preventDefault();
            toggleSidebar();
        }
    });

    // Pause/Play button
    controls.pauseButton.addEventListener('click', () => {
        emit('rotation:toggle-play');

        if (app.isPlaying) {
            controls.pauseButton.textContent = 'Pause Rotation';
            controls.pauseButton.classList.remove('paused');
        } else {
            controls.pauseButton.textContent = 'Start Rotation';
            controls.pauseButton.classList.add('paused');
            // Speed preservation: Don't zero out speeds - just pause via isPlaying flag
            // This matches Python behavior and allows proper resume
        }
    });

    // Reset button - only resets angles, not speeds (matches Python behavior)
    controls.resetButton.addEventListener('click', () => {
        emit('rotation:reset');
        controls.pauseButton.textContent = 'Start Rotation';
        controls.pauseButton.classList.add('paused');
    });

    // Randomize colors button
    controls.randomizeButton.addEventListener('click', () => {
        emit('colors:randomize');
    });

    // Projection selector
    controls.projectionButtons.forEach((button) => {
        button.addEventListener('click', () => {
            const projection = button.dataset.projection;

            // Update active state
            controls.projectionButtons.forEach((btn) => {
                btn.classList.remove('active');
            });
            button.classList.add('active');

            // Set projection via event bus
            emit('projection:change', { name: projection });
        });
    });

    console.log('✓ UI Controls initialized');

    // Store controls in app for angle updates in main animate loop
    app.controls = controls;

    // Expose toggleSidebar for console testing
    window.toggleSidebar = toggleSidebar;

    return controls;
}
